import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { theme, pageTitleStyle, pageTitleTextStyle, primaryButtonStyle } from '../../theme';

const ORANGE = theme.orange;

const Icon = ({ size = 16, color = 'currentColor', d, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    {children || <path d={d} />}
  </svg>
);
const I = {
  users: (c, s) => (<Icon size={s} color={c}><path d="M9 7a4 4 0 1 0 0 0M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"/></Icon>),
  folder:(c, s) => (<Icon size={s} color={c}><path d="M5 4h4l3 3h7a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-11a2 2 0 0 1 2 -2"/></Icon>),
  coins: (c, s) => (<Icon size={s} color={c}><path d="M9 14c0 1.66 2.69 3 6 3s6 -1.34 6 -3s-2.69 -3 -6 -3s-6 1.34 -6 3zM9 14v4c0 1.66 2.69 3 6 3s6 -1.34 6 -3v-4M3 6c0 1.13 1.25 2.17 3.28 2.74M3 6c0 -1.66 2.69 -3 6 -3s6 1.34 6 3M3 6v10c0 1.13 1.25 2.17 3.28 2.74"/></Icon>),
  alert: (c, s) => (<Icon size={s} color={c}><path d="M12 9v4M10.36 3.59l-8.1 13.53a1.9 1.9 0 0 0 1.63 2.88h16.22a1.9 1.9 0 0 0 1.63 -2.87l-8.1 -13.54a1.9 1.9 0 0 0 -3.27 0zM12 16h.01"/></Icon>),
  file:  (c, s) => (<Icon size={s} color={c}><path d="M14 3v4a1 1 0 0 0 1 1h4M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"/></Icon>),
  check: (c, s) => (<Icon size={s} color={c}><path d="M9 12l2 2l4 -4M12 3a12 12 0 0 0 8.5 3a12 12 0 0 1 -8.5 15a12 12 0 0 1 -8.5 -15a12 12 0 0 0 8.5 -3"/></Icon>),
  plus:  (c, s) => (<Icon size={s} color={c}><path d="M12 5v14M5 12h14"/></Icon>),
};

const STATUT_STYLE = {
  attente_validation:        { bg:'#FAEEDA', color:'#854F0B', icon:'check', label:'dash_st_validation' },
  attente_pieces:            { bg:'#FEF3E2', color:'#9A5A00', icon:'file',  label:'dash_st_pieces' },
  prise_en_charge_demandee:  { bg:'#E6F1FB', color:'#185FA5', icon:'folder',label:'dash_st_demande' },
};

export default function Dashboard() {
  const { t } = useLanguage();
  const { user, isArcus } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('arcus_token');
    fetch('/api/dashboard/stats', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setStats(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const fmtEur = v => {
    if (v == null) return '—';
    if (v >= 1e6) return (v / 1e6).toLocaleString('fr-FR', { maximumFractionDigits: 2 }) + ' M€';
    if (v >= 1e3) return (v / 1e3).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' k€';
    return Math.round(v).toLocaleString('fr-FR') + ' €';
  };

  const prenom = user?.prenom || user?.nom || (user?.email || '').split('@')[0];
  const s = stats || {};
  const porte = s.portefeuille || { rc_pro: 0, gf: 0 };
  const totalPorte = porte.rc_pro + porte.gf;
  const rcPct = totalPorte ? Math.round((porte.rc_pro / totalPorte) * 100) : 0;
  const gfPct = 100 - rcPct;

  const KPI = ({ icon, label, value, sub, subColor }) => (
    <div style={{ background:'#fff', border:'0.5px solid #E8E8E5', borderRadius:12, padding:'22px 24px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#64748B', marginBottom:12 }}>
        {I[icon](ORANGE, 17)} {label}
      </div>
      <div style={{ fontSize:34, fontWeight:600, color:'#1A1A1A', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:12, color: subColor || '#94A3B8', marginTop:8 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ flex:1, background:'#F7F7F5', minHeight:'100vh', overflow:'auto' }}>

      {/* En-tête : titre souligné (style "Ligne") */}
      <div style={{ padding:'24px 32px 0' }}>
        <div style={{ ...pageTitleStyle, marginBottom:0 }}>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:12, color:theme.textTertiary, letterSpacing:'.04em' }}>{t('dashboard.title')}</div>
            <div style={pageTitleTextStyle}>
              {t('dashboard.greeting')}, {prenom}
            </div>
          </div>
          {isArcus && (
            <button onClick={() => navigate('/clients/nouveau')}
              style={{ ...primaryButtonStyle, marginLeft:'auto' }}>
              {I.plus('#fff', 17)} {t('dashboard.new_client')}
            </button>
          )}
        </div>
      </div>

      <div style={{ padding:'24px 32px', maxWidth:1400, margin:'0 auto' }}>

        {/* 3 KPIs */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px,1fr))', gap:18, marginBottom:18 }}>
          <KPI icon="users"  label={t('dashboard.clients_actifs')}  value={loading ? '…' : s.clients_actifs ?? 0}
               sub={s.devis_en_cours ? `${s.polices_actives ?? 0} ${t('dashboard.polices_actives')}` : null} />
          <KPI icon="folder" label={t('dashboard.dossiers_etude')}   value={loading ? '…' : s.dossiers_en_etude ?? 0}
               sub={s.sinistres_ouverts ? `${s.sinistres_ouverts} ${t('dashboard.sinistres_ouverts')}` : null}
               subColor="#BA7517" />
          <KPI icon="coins"  label={t('dashboard.prime_annuelle')}   value={loading ? '…' : fmtEur(s.prime_annuelle)} />
        </div>

        {/* Dossiers à traiter + Portefeuille */}
        <div style={{ display:'grid', gridTemplateColumns:'1.5fr 1fr', gap:18 }}>

          <div style={{ background:'#fff', border:'0.5px solid #E8E8E5', borderRadius:12, padding:'22px 24px' }}>
            <div style={{ fontSize:15, fontWeight:600, color:'#1A1A1A', marginBottom:16 }}>{t('dashboard.a_traiter')}</div>
            {loading ? (
              <div style={{ fontSize:13, color:'#94A3B8', padding:'12px 0' }}>{t('common.loading')}</div>
            ) : (s.a_traiter && s.a_traiter.length) ? s.a_traiter.map((d, i) => {
              const st = STATUT_STYLE[d.statut] || STATUT_STYLE.prise_en_charge_demandee;
              return (
                <div key={d.id}
                  onClick={() => navigate(`/sinistres/${d.procedure_id}/dossiers/${d.id}`)}
                  style={{ display:'flex', alignItems:'center', gap:13, padding:'13px 0', cursor:'pointer',
                    borderBottom: i < s.a_traiter.length - 1 ? '0.5px solid #F1F1EE' : 'none' }}>
                  <div style={{ width:38, height:38, borderRadius:9, background:st.bg,
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {I[st.icon](st.color, 19)}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:600, color:'#1A1A1A', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {d.numero_dossier} · {d.nom_voyageur || '—'}
                    </div>
                    <div style={{ fontSize:12, color:'#64748B', marginTop:1 }}>{t('sinistres.dos_statut_' + d.statut)}</div>
                  </div>
                  <span style={{ fontSize:11, color:st.color, background:st.bg, padding:'4px 11px', borderRadius:99, fontWeight:600, flexShrink:0 }}>
                    {t('dashboard.' + st.label)}
                  </span>
                </div>
              );
            }) : (
              <div style={{ fontSize:13, color:'#94A3B8', padding:'16px 0', display:'flex', alignItems:'center', gap:8 }}>
                {I.check('#1D9E75', 18)} {t('dashboard.rien_a_traiter')}
              </div>
            )}
          </div>

          <div style={{ background:'#fff', border:'0.5px solid #E8E8E5', borderRadius:12, padding:'22px 24px' }}>
            <div style={{ fontSize:15, fontWeight:600, color:'#1A1A1A', marginBottom:16 }}>{t('dashboard.portefeuille')}</div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', marginBottom:18 }}>
              <svg width="150" height="150" viewBox="0 0 42 42">
                <circle cx="21" cy="21" r="15.9" fill="none" stroke="#F1F1EE" strokeWidth="5"/>
                {totalPorte > 0 && <>
                  <circle cx="21" cy="21" r="15.9" fill="none" stroke={ORANGE} strokeWidth="5"
                    strokeDasharray={`${rcPct} ${100 - rcPct}`} strokeDashoffset="25"/>
                  <circle cx="21" cy="21" r="15.9" fill="none" stroke="#1A1A1A" strokeWidth="5"
                    strokeDasharray={`${gfPct} ${100 - gfPct}`} strokeDashoffset={`${100 - rcPct + 25}`}/>
                </>}
                <text x="21" y="20" textAnchor="middle" fontSize="7" fontWeight="600" fill="#1A1A1A">{totalPorte}</text>
                <text x="21" y="26" textAnchor="middle" fontSize="3" fill="#64748B">{t('dashboard.polices')}</text>
              </svg>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, marginBottom:7 }}>
              <span style={{ width:10, height:10, borderRadius:3, background:ORANGE }}></span>
              <span style={{ flex:1, color:'#64748B' }}>{t('nav.polices')}</span>
              <span style={{ fontWeight:600, color:'#1A1A1A' }}>{porte.rc_pro}</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12 }}>
              <span style={{ width:10, height:10, borderRadius:3, background:'#1A1A1A' }}></span>
              <span style={{ flex:1, color:'#64748B' }}>{t('nav.gf')}</span>
              <span style={{ fontWeight:600, color:'#1A1A1A' }}>{porte.gf}</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
